import { useState, useRef, useEffect } from 'react';
import { Plus, Clock, Target } from 'lucide-react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/Select';
import { Badge } from '../ui/Badge';
import { useTemplatesStore } from '@/store/templatesStore';
import { cn } from '@/utils/cn';

interface TemplateQuickItemEntryProps {
  templateId: string;
  onItemAdded?: () => void;
  placeholder?: string;
  autoFocus?: boolean;
  className?: string;
}

export function TemplateQuickItemEntry({
  templateId,
  onItemAdded,
  placeholder = "Add a new item to this template...",
  autoFocus = false,
  className
}: TemplateQuickItemEntryProps) {
  const [content, setContent] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);
  const [estimatedMinutes, setEstimatedMinutes] = useState<number | undefined>();
  const [difficulty, setDifficulty] = useState<'easy' | 'medium' | 'hard' | undefined>();
  const [isLoading, setIsLoading] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const { addTemplateItem } = useTemplatesStore();

  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!content.trim() || isLoading) return;

    setIsLoading(true);

    try {
      const itemData = {
        estimatedMinutes,
        difficulty,
      };

      await addTemplateItem(templateId, content.trim(), itemData);

      // Reset form
      setContent('');
      setEstimatedMinutes(undefined);
      setDifficulty(undefined);
      setIsExpanded(false);

      // Focus back on input
      if (inputRef.current) {
        inputRef.current.focus();
      }

      onItemAdded?.();
      console.log('✅ Template item added successfully');
    } catch (error) {
      console.error('Failed to add template item:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    } else if (e.key === 'Escape') {
      setIsExpanded(false);
      setContent('');
    }
  };

  const getDifficultyColor = (diff?: string) => {
    switch (diff) {
      case 'easy': return 'text-green-600 bg-green-100';
      case 'medium': return 'text-yellow-600 bg-yellow-100';
      case 'hard': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  return (
    <div className={cn('bg-white border border-gray-200 rounded-lg p-4', className)}>
      <form onSubmit={handleSubmit} className="space-y-3">
        {/* Main Input */}
        <div className="flex gap-2">
          <div className="flex-1">
            <Input
              data-testid="add-template-item-input"
              ref={inputRef}
              value={content}
              onChange={(e) => setContent(e.target.value)}
              onKeyDown={handleKeyPress}
              onFocus={() => setIsExpanded(true)}
              placeholder={placeholder}
              disabled={isLoading}
              className="border-0 focus:bg-white focus:ring-1 focus:ring-blue-500"
            />
          </div>
          <Button
            type="submit"
            disabled={!content.trim() || isLoading}
            size="sm"
            className="flex-shrink-0"
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>

        {/* Expanded Options */}
        {isExpanded && (
          <div className="space-y-3 pt-2 border-t border-gray-100">
            {/* Quick Actions Row */}
            <div className="flex items-center gap-2 flex-wrap">
              {/* Estimated Time */}
              <div className="flex items-center gap-1">
                <Clock className="h-4 w-4 text-gray-400" />
                <Input
                  type="number"
                  value={estimatedMinutes || ''}
                  onChange={(e) => setEstimatedMinutes(e.target.value ? parseInt(e.target.value) : undefined)}
                  placeholder="Minutes"
                  className="w-20 h-8 text-xs"
                  min="1"
                  max="999"
                />
              </div>

              {/* Difficulty */}
              <div className="flex items-center gap-1">
                <Target className="h-4 w-4 text-gray-400" />
                <Select value={difficulty || ''} onValueChange={(value) => setDifficulty(value as 'easy' | 'medium' | 'hard' | undefined)}>
                  <SelectTrigger className="w-24 h-8 text-xs">
                    <SelectValue placeholder="Difficulty" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">None</SelectItem>
                    <SelectItem value="easy">Easy</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="hard">Hard</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Preview */}
            {(difficulty || estimatedMinutes) && (
              <div className="flex items-center gap-2 text-xs text-gray-600 p-2 rounded">
                <span>Preview:</span>
                {difficulty && (
                  <Badge className={cn('text-xs', getDifficultyColor(difficulty))}>
                    {difficulty}
                  </Badge>
                )}
                {estimatedMinutes && (
                  <span className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {estimatedMinutes}m
                  </span>
                )}
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-between items-center">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  setIsExpanded(false);
                  setContent('');
                  setEstimatedMinutes(undefined);
                  setDifficulty(undefined);
                }}
                className="text-xs"
              >
                Cancel
              </Button>

              <div className="text-xs text-gray-500">
                Press Enter to add • Escape to cancel
              </div>
            </div>
          </div>
        )}
      </form>
    </div>
  );
}
